<template>
  <view
    v-if="visible"
    class="ai-pet"
    :class="[
      `dock-${dockSide}`,
      {
        dragging: dragging,
        docked: isDocked,
        animating: animating,
      },
    ]"
    :style="petStyle"
    @touchstart.stop="onTouchStart"
    @touchmove.stop.prevent="onTouchMove"
    @touchend.stop="onTouchEnd"
    @touchcancel.stop="onTouchEnd"
    @tap.stop="onTap"
  >
    <!-- 完整桌宠：Lanhu 绿盔猴头 -->
    <view class="ai-pet-body" :class="{ hide: isDocked }">
      <image class="ai-pet-img" :src="PET_SRC" mode="aspectFit" />
    </view>

    <!-- 贴边收起 UI -->
    <view class="ai-pet-dock" :class="{ show: isDocked }">
      <view class="dock-chip">
        <image class="dock-img" :src="PET_SRC" mode="aspectFill" />
      </view>
    </view>
  </view>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

type DockSide = 'none' | 'left' | 'right' | 'top' | 'bottom';

withDefaults(
  defineProps<{
    visible?: boolean;
  }>(),
  { visible: true }
);

const emit = defineEmits<{ (e: 'open'): void }>();

/** 桌宠切图：绿盔猴头（对齐稿面浮层） */
const PET_SRC = '/static/image/lanhu/common/ai-pet.webp';

/** 模块级位置：跨页面/显隐切换不丢失 */
const persisted = {
  inited: false,
  x: 0,
  y: 0,
  dockSide: 'none' as DockSide,
  isDocked: false,
};

/** 视口尺寸：贴边/拖动相对「内容区」宽度（与 --app-max-width 对齐），不是整屏 */
const getViewport = () => {
  const s = uni.getSystemInfoSync();
  const fullW = s.windowWidth || s.screenWidth || 375;
  const h = s.windowHeight || s.screenHeight || 667;
  let w = fullW;

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--app-max-width')
      .trim();
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed) && parsed > 0) {
      w = Math.min(fullW, parsed);
    } else {
      w = Math.min(fullW, 750);
    }
  }

  return { w, h };
};

let { w: windowW, h: windowH } = getViewport();

/** 逻辑尺寸（px） */
const PET = 64;
const EDGE_PAD = 8;
const DOCK_REVEAL = 22;
const DOCK_THRESHOLD = 36;
const MOVE_THRESHOLD = 8;

const defaultX = () => windowW - PET - EDGE_PAD;
const defaultY = () => Math.round(windowH * 0.42);

const petX = ref(persisted.inited ? persisted.x : defaultX());
const petY = ref(persisted.inited ? persisted.y : defaultY());
const dockSide = ref<DockSide>(persisted.inited ? persisted.dockSide : 'none');
const isDocked = ref(persisted.inited ? persisted.isDocked : false);
const dragging = ref(false);
const animating = ref(false);
const moved = ref(false);

const startX = ref(0);
const startY = ref(0);
const startPetX = ref(0);
const startPetY = ref(0);

const persistPose = () => {
  persisted.inited = true;
  persisted.x = petX.value;
  persisted.y = petY.value;
  persisted.dockSide = dockSide.value;
  persisted.isDocked = isDocked.value;
};

watch([petX, petY, dockSide, isDocked], persistPose);

/** 拖动范围：允许略超出视口，便于触发四边贴合 */
const clampX = (x: number) => Math.max(-(PET - 4), Math.min(windowW - 4, x));
const clampY = (y: number) => Math.max(-(PET - 4), Math.min(windowH - 4, y));

/** 完全可见时的视口内约束 */
const clampVisibleX = (x: number) => Math.max(EDGE_PAD, Math.min(windowW - PET - EDGE_PAD, x));
const clampVisibleY = (y: number) => Math.max(EDGE_PAD, Math.min(windowH - PET - EDGE_PAD, y));

const petStyle = computed(() => ({
  left: `${petX.value}px`,
  top: `${petY.value}px`,
  transition: dragging.value || !animating.value ? 'none' : 'left 0.28s cubic-bezier(0.22, 1, 0.36, 1), top 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
}));

const undock = () => {
  isDocked.value = false;
  dockSide.value = 'none';
};

const applyDock = (side: DockSide) => {
  animating.value = true;
  dockSide.value = side;
  isDocked.value = true;

  // 贴合屏幕视口四边：大半藏出视口，只留 DOCK_REVEAL
  if (side === 'left') {
    petX.value = -(PET - DOCK_REVEAL);
    petY.value = clampVisibleY(petY.value);
  } else if (side === 'right') {
    petX.value = windowW - DOCK_REVEAL;
    petY.value = clampVisibleY(petY.value);
  } else if (side === 'top') {
    petY.value = -(PET - DOCK_REVEAL);
    petX.value = clampVisibleX(petX.value);
  } else if (side === 'bottom') {
    petY.value = windowH - DOCK_REVEAL;
    petX.value = clampVisibleX(petX.value);
  }

  setTimeout(() => {
    animating.value = false;
  }, 300);
};

/** 相对视口四边的距离，取最近边 */
const resolveDockSide = (): DockSide => {
  const distL = petX.value;
  const distR = windowW - (petX.value + PET);
  const distT = petY.value;
  const distB = windowH - (petY.value + PET);

  const edges: { side: DockSide; dist: number }[] = [
    { side: 'left', dist: distL },
    { side: 'right', dist: distR },
    { side: 'top', dist: distT },
    { side: 'bottom', dist: distB },
  ];
  edges.sort((a, b) => a.dist - b.dist);
  const nearest = edges[0];
  if (nearest.dist <= DOCK_THRESHOLD) return nearest.side;
  return 'none';
};

/** 未贴边时松手：吸附到较近的左右视口边，保持完全可见 */
const snapToSide = () => {
  animating.value = true;
  const mid = petX.value + PET / 2;
  if (mid < windowW / 2) {
    petX.value = EDGE_PAD;
  } else {
    petX.value = windowW - PET - EDGE_PAD;
  }
  petY.value = clampVisibleY(petY.value);
  setTimeout(() => {
    animating.value = false;
  }, 280);
};

const onTouchStart = (e: any) => {
  const vp = getViewport();
  windowW = vp.w;
  windowH = vp.h;
  dragging.value = true;
  moved.value = false;
  animating.value = false;
  const t = e.touches?.[0] || e.changedTouches?.[0];
  startX.value = t?.clientX ?? 0;
  startY.value = t?.clientY ?? 0;
  startPetX.value = petX.value;
  startPetY.value = petY.value;
};

const onTouchMove = (e: any) => {
  if (!dragging.value) return;
  const t = e.touches?.[0] || e.changedTouches?.[0];
  const x = t?.clientX ?? startX.value;
  const y = t?.clientY ?? startY.value;
  const dx = x - startX.value;
  const dy = y - startY.value;

  if (!moved.value && (Math.abs(dx) > MOVE_THRESHOLD || Math.abs(dy) > MOVE_THRESHOLD)) {
    moved.value = true;
    if (isDocked.value) {
      undock();
      petX.value = clampVisibleX(startX.value - PET / 2);
      petY.value = clampVisibleY(startY.value - PET / 2);
      startPetX.value = petX.value;
      startPetY.value = petY.value;
      startX.value = x;
      startY.value = y;
      return;
    }
  }

  if (!moved.value) return;
  petX.value = clampX(startPetX.value + dx);
  petY.value = clampY(startPetY.value + dy);
};

const onTouchEnd = () => {
  if (!dragging.value) return;
  dragging.value = false;

  if (!moved.value) return;

  const side = resolveDockSide();
  if (side !== 'none') {
    applyDock(side);
  } else {
    undock();
    snapToSide();
  }
};

const expandFromDock = () => {
  animating.value = true;
  isDocked.value = false;
  if (dockSide.value === 'left') {
    petX.value = EDGE_PAD;
  } else if (dockSide.value === 'right') {
    petX.value = windowW - PET - EDGE_PAD;
  } else if (dockSide.value === 'top') {
    petY.value = EDGE_PAD;
  } else if (dockSide.value === 'bottom') {
    petY.value = windowH - PET - EDGE_PAD;
  }
  petX.value = clampVisibleX(petX.value);
  petY.value = clampVisibleY(petY.value);
  dockSide.value = 'none';
  setTimeout(() => {
    animating.value = false;
  }, 280);
};

const onTap = () => {
  if (moved.value) return;
  if (isDocked.value) {
    expandFromDock();
    return;
  }
  emit('open');
};

onMounted(() => {
  const vp = getViewport();
  windowW = vp.w;
  windowH = vp.h;

  // 每次进入保证落在内容区右侧可见位置（避免贴边收起后用户以为消失）
  undock();
  petX.value = defaultX();
  petY.value = clampVisibleY(defaultY());
  persistPose();
});
</script>

<style scoped lang="scss">
.ai-pet {
  position: absolute;
  z-index: 1;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
  pointer-events: auto;

  &.dragging {
    opacity: 0.92;
  }
}

.ai-pet-body {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transform: scale(1);
  transition: opacity 0.2s ease, transform 0.2s ease;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.18));

  &.hide {
    opacity: 0;
    transform: scale(0.55);
    pointer-events: none;
  }
}

.ai-pet-img {
  width: 64px;
  height: 64px;
  display: block;
}

/* 贴边收起条 */
.ai-pet-dock {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  transform: scale(0.85);
  transition: opacity 0.22s ease, transform 0.22s ease;

  &.show {
    opacity: 1;
    pointer-events: auto;
    transform: scale(1);
  }
}

.dock-chip {
  position: absolute;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.dock-img {
  width: 48px;
  height: 48px;
  display: block;
}

.ai-pet.dock-left .dock-chip {
  top: 8px;
  right: 0;
  width: 28px;
  height: 48px;
  border-radius: 0 16px 16px 0;
}

.ai-pet.dock-right .dock-chip {
  top: 8px;
  left: 0;
  width: 28px;
  height: 48px;
  border-radius: 16px 0 0 16px;
}

.ai-pet.dock-top .dock-chip {
  left: 8px;
  bottom: 0;
  width: 48px;
  height: 28px;
  border-radius: 0 0 16px 16px;
}

.ai-pet.dock-bottom .dock-chip {
  left: 8px;
  top: 0;
  width: 48px;
  height: 28px;
  border-radius: 16px 16px 0 0;
}
</style>
